import { createApi } from "@reduxjs/toolkit/query/react";

import apiConfig from "configs/apiConfig";
import { REDUCER_PATHS } from "store/config";
import { transformResponseHandler } from "utils/transformResponseHandler";

import { customFetchBase } from ".";

const userApi = createApi({
  reducerPath: REDUCER_PATHS.USERS,
  baseQuery: customFetchBase,
  endpoints: (builder) => ({
    adminManagementListing: builder.query({
      query: (data) => ({
        url: `${apiConfig.LISTING_ADMIN_MANAGEMENT}?page=${data?.page}&pageSize=${data?.pageSize}&searchText=${data?.searchText}&sortColumn=${data?.sortColumn}&sortDirection=${data?.sortDirection}&filter=${data?.filter}`,
        method: "GET",
      }),
    }),
    addAdminUser: builder.mutation({
      query: (data) => ({
        url: apiConfig.ADMIN_MANAGEMENT,
        body: data,
        method: "POST",
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    editAdminUser: builder.mutation({
      query: (data) => ({
        url: `${apiConfig.ADMIN_MANAGEMENT}/${data?.id}`,
        body: data?.postData,
        method: "PUT",
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    deleteAdminUser: builder.mutation({
      query: (data) => ({
        url: apiConfig.ADMIN_MANAGEMENT,
        body: data,
        method: "DELETE",
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    statusChangeAdminUser: builder.mutation({
      query: (data) => ({
        url: apiConfig.ADMIN_MANAGEMENT,
        body: data,
        method: "PATCH",
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    detailsAdminUser: builder.query({
      query: (data) => ({
        url: `${apiConfig.ADMIN_MANAGEMENT}/${data.id}`,
        method: "GET",
      }),
    }),
    userManagementListing: builder.query({
      query: (data) => ({
        url: `${apiConfig.LISTING_USER_MANAGEMENT}?page=${data?.page}&pageSize=${data?.pageSize}&searchText=${data?.searchText}&sortColumn=${data?.sortColumn}&sortDirection=${data?.sortDirection}&filter=${data?.filter}`,
        method: "GET",
      }),
    }),
    addUser: builder.mutation({
      query: (data) => ({
        url: apiConfig.USER_MANAGEMENT,
        body: data?.postData || data,
        method: "POST",
      }),
      invalidatesTags: ["userDetails"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    editUser: builder.mutation({
      query: (data) => ({
        url: apiConfig.USER_MANAGEMENT + "/" + data?.userId,
        body: data?.postData,
        method: "PUT",
      }),
      invalidatesTags: ["userDetails"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    deleteUser: builder.mutation({
      query: (data) => ({
        url: apiConfig.USER_MANAGEMENT,
        body: data,
        method: "DELETE",
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    changeUserStatus: builder.mutation({
      query: (data) => ({
        url: apiConfig.USER_MANAGEMENT,
        body: data,
        method: "PATCH",
      }),
      invalidatesTags: ["userDetails"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    userDetails: builder.query({
      query: (data) => ({
        url: `${apiConfig.USER_MANAGEMENT}?userId=${data?.userId}`,
        method: "GET",
      }),
      providesTags: ["userDetails"],
    }),
    userDetailsDownload: builder.mutation({
      query: (data) => ({
        url: `${apiConfig.USER_MANAGEMENT_DOWNLOAD}/${data.type}?languageId=${data?.languageId}&filter=${data.filter}&column=${data.column}`,
        method: "GET",
      }),
    }),
    adminUserDetailsDownload: builder.mutation({
      query: (data) => ({
        url: `${apiConfig.ADMIN_USERS_DOWNLOAD}/${data.type}?filter=${data.filter}&column=${data.column}`,
        method: "GET",
      }),
    }),
    userListing: builder.mutation({
      query: (data) => ({
        url: apiConfig.LISTING_USER_MANAGEMENT,
        body: data,
        method: "POST",
      }),
      invalidatesTags: ["userDetails"],
    }),
    revokeSuspension: builder.mutation({
      query: (data) => ({
        url: apiConfig.USER_REVOKE_SUSPENSION,
        body: data,
        method: "POST",
      }),
      invalidatesTags: ["userDetails"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    userReportListing: builder.query({
      query: (data) => ({
        url: `${apiConfig.USER_REPORT_LISTING}?page=${data?.page}&pageSize=${data?.pageSize}&searchText=${data?.searchText}&sortColumn=${data?.sortColumn}&sortDirection=${data?.sortDirection}`,
        method: "GET",
      }),
    }),
    userReportDownload: builder.mutation({
      query: (data) => ({
        url: apiConfig.USER_REPORT_DOWNLOAD,
        body: data,
        method: "POST",
      }),
    }),
    userCancelSuspension: builder.mutation({
      query: (data) => ({
        url: apiConfig.USER_REPORT_CANCEL_SUSPENSION,
        body: data,
        method: "POST",
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    userSuspend: builder.mutation({
      query: (data) => ({
        url: apiConfig.USER_REPORT_SUSPEND,
        body: data,
        method: "POST",
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    userRevokeSuspension: builder.mutation({
      query: (data) => ({
        url: apiConfig.USER_REPORT_REVOKE_SUSPENSION,
        body: data,
        method: "POST",
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),

    // App user
    addEditAppUser: builder.mutation({
      query: (data) => ({
        url: apiConfig.APP_USER,
        body: data,
        method: "POST",
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    EditAppUser: builder.mutation({
      query: (data) => ({
        url: apiConfig.APP_USER,
        body: data,
        method: "PUT",
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    appUserListing: builder.query({
      query: (data) => ({
        url: `${apiConfig.APP_USER}/listing?page=${data?.page}&pageSize=${data?.pageSize}&searchText=${data?.searchText}&sortColumn=${data?.sortColumn}&sortDirection=${data?.sortDirection}&filter=${data?.filter}`,
        method: "GET",
      }),
      providesTags: ["appUser"],
    }),
    deleteAppUser: builder.mutation({
      query: (data) => ({
        url: apiConfig.APP_USER,
        body: data,
        method: "DELETE",
      }),
      invalidatesTags: ["appUser"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    changeAppUserStatus: builder.mutation({
      query: (data) => ({
        url: apiConfig.APP_USER,
        body: data,
        method: "PATCH",
      }),
      invalidatesTags: ["appUser"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    detailsAppUser: builder.query({
      query: (data) => ({
        url: `${apiConfig.APP_USER}?userId=${data?.userId}`,
        method: "GET",
      }),
      providesTags: ["appUser"],
    }),
    assignTerritoryAppUser: builder.mutation({
      query: (data) => ({
        url: `${apiConfig.APP_USER}/assign-territory`,
        body: data,
        method: "PATCH",
      }),
      invalidatesTags: ["appUser"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    appUserDetailsDownload: builder.mutation({
      query: (data) => ({
        url: `${apiConfig.APP_USER}/download/${data.type}?filter=${data.filter}&column=${data.column}`,
        method: "GET",
      }),
    }),
    changeAppUserPassword: builder.mutation({
      query: (data) => ({
        url: apiConfig.APP_USER_PASSWORD_CHANGE,
        body: data,
        method: "POST",
      }),
      invalidatesTags: ["appUser"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    // Patients
    addPatients: builder.mutation({
      query: (data) => ({
        url: apiConfig.PATIENTS,
        body: data,
        method: "POST",
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    EditPatients: builder.mutation({
      query: (data) => ({
        url: apiConfig.PATIENTS,
        body: data,
        method: "PUT",
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    patientsListing: builder.query({
      query: (data) => ({
        url: `${apiConfig.PATIENTS}/listing?page=${data?.page}&pageSize=${data?.pageSize}&searchText=${data?.searchText}&sortColumn=${data?.sortColumn}&sortDirection=${data?.sortDirection}&filter=${data?.filter}`,
        method: "GET",
      }),
      providesTags: ["patient"],
    }),
    patientVisitListing: builder.query({
      query: (data) => ({
        url: `${apiConfig.PATIENTS}/visit-lists?patientId=${data?.patientId}`,
        method: "GET",
      }),
      providesTags: ["patient"],
    }),
    deletePatients: builder.mutation({
      query: (data) => ({
        url: apiConfig.PATIENTS,
        body: data,
        method: "DELETE",
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    changePatientStatus: builder.mutation({
      query: (data) => ({
        url: apiConfig.PATIENTS,
        body: data,
        method: "PATCH",
      }),
      invalidatesTags: ["patient"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    detailsPatients: builder.query({
      query: (data) => ({
        url: `${apiConfig.PATIENTS}?patientId=${data?.patientId}`,
        method: "GET",
      }),
      providesTags: ["patient"],
    }),
    patientsDetailsDownload: builder.mutation({
      query: (data) => ({
        url: `${apiConfig.PATIENTS}/download/${data.type}?filter=${data.filter}&column=${data.column}`,
        method: "GET",
      }),
    }),

    //Territory Listing
    assignTerritoryPatients: builder.mutation({
      query: (data) => ({
        url: `${apiConfig.PATIENTS}/assign-territory`,
        body: data,
        method: "PATCH",
      }),
      invalidatesTags: ["patient"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
  }),
});

export const {
  useLazyAdminManagementListingQuery,
  useAddAdminUserMutation,
  useDeleteAdminUserMutation,
  useStatusChangeAdminUserMutation,
  useDetailsAdminUserQuery,
  useLazyUserManagementListingQuery,
  useAddUserMutation,
  useDeleteUserMutation,
  useChangeUserStatusMutation,
  useLazyUserDetailsQuery,
  useUserDetailsDownloadMutation,
  useAdminUserDetailsDownloadMutation,
  useEditUserMutation,
  useEditAdminUserMutation,
  //POST USER DATA
  useUserListingMutation,
  useRevokeSuspensionMutation,
  useLazyUserReportListingQuery,
  useUserReportDownloadMutation,
  useUserCancelSuspensionMutation,
  useUserSuspendMutation,
  useUserRevokeSuspensionMutation,
  //AppUser
  useAddEditAppUserMutation,
  useLazyAppUserListingQuery,
  useDeleteAppUserMutation,
  useChangeAppUserStatusMutation,
  useLazyDetailsAppUserQuery,
  useEditAppUserMutation,
  useAssignTerritoryAppUserMutation,
  useAppUserDetailsDownloadMutation,
  useChangeAppUserPasswordMutation,
  //Patients
  useAddPatientsMutation,
  useEditPatientsMutation,
  useDeletePatientsMutation,
  useChangePatientStatusMutation,
  usePatientsDetailsDownloadMutation,
  useLazyDetailsPatientsQuery,
  useLazyPatientsListingQuery,
  useAssignTerritoryPatientsMutation,
  useLazyPatientVisitListingQuery,
} = userApi;

export default userApi;
